
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Controls } from '../backtesting/Controls';
import { BackTestResultRow } from '../backtesting/BackTestResultRow';
import { BacktestModal } from '../../components/modals/BacktestChartModal/BacktestModal';
import { BackTestRunEventModal } from '../../components/modals/BackTestRunEventModal/BackTestRunEventModal';
import { useFilterResults } from '../../utils/useFilterResults';
import { LottieLoading } from '../../components/placeholder/LottieLoading';
import { useEffect, useState } from 'react';
import { BackTest } from '../../interface/BackTest';
import { BackTestApi } from '../../api/BackTestApi';
import moment from 'moment';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { dashboardMutedTextSx, getDashboardPanelSx } from '../dashboardv2/dashboardStyles';

interface SystemBacktestingTableProps
{
  ownTests: string
}

export const SystemBacktestingTable = ({ ownTests }: SystemBacktestingTableProps) => {
  const theme = useTheme()

  const isNothingFound = () => !loading && filteredResults && filteredResults?.length === 0
  const isSomethingFound = () => !loading && filteredResults && filteredResults?.length > 0
  const [ results, setResults ] = useState<BackTest[] | undefined>([])
  const [ loading, setLoading ] = useState(false)
  const [ showBacktestModal, setShowBacktestModal ] = useState(false)
  const [ showRunEvents, setShowRunEvents ] = useState<BackTest | undefined>(undefined)
  const [ query, setQuery ] = useState("")

  const [ order, setOrder ] = useState<string>("newest")
  const [ fromTime, setFromTime ] = useState(8)

  const [ filteredResults ] = useFilterResults(query, results);

  const clearFilters = () =>
    {
      setOrder("newest")
      setFromTime(8)
    }

  const getBackTestResults = async () =>
    {
  
      setLoading(true)
      const btApi = new BackTestApi()
      const results = await btApi.getResults("all", "all", moment().subtract(fromTime, 'hours').toISOString(), order, ownTests);
      setResults(results)
      setLoading(false)
    }

    useEffect(() => {

      getBackTestResults()
  
    }, [ order, fromTime ])
    


  return (
  <Box sx={{ ...getDashboardPanelSx(theme), p: { xs: 2, md: 2.5 } }}>
  <Typography variant="overline" sx={{ letterSpacing: 1.5, ...dashboardMutedTextSx(theme) }}>
    {ownTests === "yes" ? "My Result History" : "System Result Feed"}
  </Typography>
  <Typography sx={{ fontSize: 28, lineHeight: 1.15, mb: 2 }}>
    {ownTests === "yes" ? "Review and sort your custom backtest runs" : "Compare the latest system-generated backtest outcomes"}
  </Typography>

  <Controls
      setQuery={setQuery}
      clearFilters={clearFilters}
      showChart={setShowBacktestModal}
      timeRange={fromTime}
      order={order}
      onOrderChange={setOrder}
      onTimeRangeChange={setFromTime}
     />

    { loading && <LottieLoading loop animation={require('../../animations/backtesting-lottie.json')} width={200} height={200} text='Getting Backtesting Results'/>} 

    { isNothingFound() && <LottieLoading loop={false} animation={require('../../animations/not-found-lottie.json')} width={200} height={200} text={`Looks like there's no results`}/>} 

    { isSomethingFound() && 
 
       <TableContainer
         component={Paper}
         sx={{
           ...styles.table,
           borderRadius: "12px",
           border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.08 : 0.6)}`,
           backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.03 : 0.7),
         }}
       >
       <Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader >
         <TableHead>
           <TableRow>
             <TableCell sx={styles.header(theme)}>Run start time</TableCell>
             { ownTests === "yes" && <TableCell align="right" sx={styles.header(theme)}>Test Config Name</TableCell> }
             <TableCell align="right" sx={styles.header(theme)}>Trading Pair</TableCell>
             <TableCell align="right" sx={styles.header(theme)}>Indicator</TableCell>
             <TableCell align="right" sx={styles.header(theme)}>Win Percentage</TableCell>
             <TableCell align="right" sx={styles.header(theme)}>Profit</TableCell>
             <TableCell align="right" sx={styles.header(theme)}>Sales</TableCell>
             <TableCell align="right" sx={styles.header(theme)}>Purchases</TableCell>
             <TableCell align="right" sx={styles.header(theme)}>Candle</TableCell>
             <TableCell align="right" sx={styles.header(theme)}>Settings</TableCell>

           </TableRow>
         </TableHead>
         <TableBody >
           {filteredResults && filteredResults.map((row, key) => <BackTestResultRow ownTests={ownTests} key={key} row={row} onShowRunEvents={setShowRunEvents} />)}
         </TableBody>
       </Table>
     </TableContainer>

    }

    <BacktestModal open={showBacktestModal} handleClose={() => setShowBacktestModal(false)} />
    <BackTestRunEventModal open={!!showRunEvents} handleClose={() => setShowRunEvents(undefined)} backtestItem={showRunEvents}/>

  </Box>)
}

const styles = {
  header: (theme: any) => ({
    fontWeight: "700",
    backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.16 : 0.03),
    backdropFilter: "blur(10px)",
  }),
  table: {
    height: "80vh",
    marginTop: 16,
    marginBottom: 28,
  }
}
